import type { ISalesCounterpart } from 'models/ISalesCounterpart';
import { batch } from 'react-redux';
import { secondaryMarketQuoteCancelled } from 'state/secondaryMarket/realtimeQuote/quote';
import { getSecondaryMarketRtFormQuoteId } from 'state/secondaryMarket/realtimeQuote/realtimeQuoteForm/realTimeQuoteForm.selectors';
import { secondaryMarketAstroClientRequested } from 'state/secondaryMarket/referential/astroClient/astroClient.actions';
import { getUserDefaultClient } from 'state/secondaryMarket/referential/astroClient/astroClient.selectors';
import { getSecondaryMarketRealtimeAstroProductFromTab } from 'state/secondaryMarket/referential/astroProduct/astroProduct.selectors';
import { findDefaultClient } from 'state/secondaryMarket/referential/bcsProduct/bcsProduct.utils';
import { TradeWay } from 'state/secondaryMarket/secondaryMarket.model';
import { fromSecondaryMarket } from 'state/selectors';
import type { Thunk } from 'state/state.common';
import { AppState } from 'state/state.model';

import { SidePanelStatus, secondaryMarketDealSplitRowNotionalChanged } from '.';
import { secondaryMarketRtFormClientSelected, secondaryMarketRtFormFieldChanged } from './realtimeQuoteForm.actions';

function isQuoteOrTradeRejected(status: SidePanelStatus) {
  return (
    status === 'QUOTE_FAILED' || status === 'QUOTE_REJECTED' || status === 'TRADE_FAILED' || status === 'TRADE_REJECTED'
  );
}
function getQuoteOrTradeRejectionStatus(state: AppState, tabId: string) {
  const getFormStatus = fromSecondaryMarket.makeGetSecondaryMarketRtFormStatus();
  const status = getFormStatus(state, tabId);
  const isRejected = isQuoteOrTradeRejected(status);
  const quoteId = getSecondaryMarketRtFormQuoteId(state, tabId);

  return {
    shouldResetSidePanel: isRejected,
    quoteId,
  };
}

export function secondaryMarketRtFormNotionalChangedThunk(tabId: string, notional: number | undefined): Thunk<void> {
  return (dispatch, getState) => {
    const state = getState();
    const denomination = getSecondaryMarketRealtimeAstroProductFromTab(state, tabId)?.data?.denomination;

    const { shouldResetSidePanel, quoteId } = getQuoteOrTradeRejectionStatus(state, tabId);
    if (shouldResetSidePanel) {
      dispatch(secondaryMarketQuoteCancelled(tabId, quoteId));
    }

    if (denomination !== undefined && notional !== undefined) {
      const quantity = Math.floor(notional / denomination);
      dispatch(secondaryMarketRtFormFieldChanged(tabId, 'quantity', quantity));
    }
    dispatch(secondaryMarketRtFormAllocateAllNotionalWhenSingleCustodianThunk(tabId));
  };
}

export function secondaryMarketRtFormQuantityChangedThunk(tabId: string, quantity: number | undefined): Thunk<void> {
  return (dispatch, getState) => {
    const state = getState();

    const { shouldResetSidePanel, quoteId } = getQuoteOrTradeRejectionStatus(state, tabId);
    if (shouldResetSidePanel) {
      dispatch(secondaryMarketQuoteCancelled(tabId, quoteId));
    }

    dispatch(secondaryMarketRtFormFieldChanged(tabId, 'quantity', quantity));
    dispatch(secondaryMarketRtFormAllocateAllNotionalWhenSingleCustodianThunk(tabId));
  };
}

export function secondaryMarketRtFormTradeWayChangedThunk(tabId: string, tradeWay: TradeWay): Thunk<void> {
  return (dispatch, getState) => {
    const state = getState();

    const { shouldResetSidePanel, quoteId } = getQuoteOrTradeRejectionStatus(state, tabId);
    if (shouldResetSidePanel) {
      dispatch(secondaryMarketQuoteCancelled(tabId, quoteId));
    }

    dispatch(secondaryMarketRtFormFieldChanged(tabId, 'tradeWay', tradeWay));
  };
}

export function secondaryMarketRtFormUpfrontFeeChangedThunk(tabId: string, hasUpfrontFee: boolean): Thunk<void> {
  return (dispatch, getState) => {
    const state = getState();

    const { shouldResetSidePanel, quoteId } = getQuoteOrTradeRejectionStatus(state, tabId);
    if (shouldResetSidePanel) {
      dispatch(secondaryMarketQuoteCancelled(tabId, quoteId));
    }

    dispatch(secondaryMarketRtFormFieldChanged(tabId, 'upfrontFee', hasUpfrontFee));
  };
}

// TODO : move the single custodian logic into a selector (derived state), so that we are sure it is up to date
export function secondaryMarketRtFormAllocateAllNotionalWhenSingleCustodianThunk(tabId: string): Thunk<void> {
  return (dispatch, getState) => {
    const state = getState();
    const hasSingleCustodian = fromSecondaryMarket.referential.client.hasSecondaryMarketSingleCustodian(state, tabId);
    if (hasSingleCustodian) {
      const notional = fromSecondaryMarket.realtimeQuote.form.getSecondaryMarketRtFormNotional(state, tabId);
      dispatch(secondaryMarketDealSplitRowNotionalChanged(tabId, 0, notional));
    }
  };
}

export function secondaryMarketRtFormClientSelectedThunk(tabId: string, client: ISalesCounterpart): Thunk<void> {
  return dispatch => {
    batch(() => {
      dispatch(secondaryMarketRtFormClientSelected(tabId, client));
      dispatch(secondaryMarketAstroClientRequested(tabId, client.id));
      dispatch(secondaryMarketRtFormAllocateAllNotionalWhenSingleCustodianThunk(tabId));
    });
  };
}
export function secondaryMarketRtFormSelectDefaultClientFromProductThunk(
  tabId: string,
  productClients: ReadonlyArray<ISalesCounterpart>,
): Thunk<void> {
  return (dispatch, getState) => {
    const state = getState();
    const selectedClient = fromSecondaryMarket.realtimeQuote.form.getSecondaryMarketRtFormClient(state, tabId);
    const defaultClient = findDefaultClient(productClients) ?? getUserDefaultClient(state);
    if (defaultClient === undefined) {
      if (selectedClient?.id !== undefined) {
        dispatch(secondaryMarketRtFormClientSelected(tabId, undefined));
      }
    } else {
      dispatch(secondaryMarketRtFormClientSelectedThunk(tabId, defaultClient));
    }
  };
}
